import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnInit,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import {
  catchError,
  combineLatest,
  concatMap,
  filter,
  finalize,
  forkJoin,
  from,
  map,
  of,
  switchMap,
  take,
  tap,
  toArray,
} from 'rxjs';
import { IDataCaptureSegment } from '../../models/data-capture.models';
import { DataCaptureService } from '../../services/data-capture.service';
import { ExternalCallDurationValidationService } from '../../services/external-call-duration-validation.service';
import { StandardiseTranscriptService } from '../../services/standardise-transcript.service';
import {
  AddRespondentFormControls,
  AddRespondentFormGroup,
  AddRespondentRowFormGroup,
  AddRespondentRowFormValue,
  AddRespondentRowsFormGroup,
  BulkImportSummary,
  BulkUploadStep,
  RowMatchStatus,
  UnassignedFile,
} from './models/add-respondent.types';
import { AddRespondentRowFormService } from './services/add-respondent-row-form.service';

type UploadResult = {
  row: AddRespondentRowFormGroup;
  status: 'success' | 'failed';
};

type StagedFileCandidate = {
  file: File;
  rawStem: string;
  matchKey: string;
  tokens: string[];
  dateKey: string;
};

type StagedRespondentRow = {
  audioFile?: File;
  transcriptFile?: File;
  dateOfCall: string;
  matchStatus: RowMatchStatus;
  matchMessage: string;
};

type FileMatch = {
  audioCandidate: StagedFileCandidate;
  transcriptCandidate: StagedFileCandidate;
  score: number;
};

type TranscriptMatchResult = {
  matched: FileMatch[];
  recordingOnly: StagedRespondentRow[];
  transcriptOnly: StagedRespondentRow[];
};

type MatchAccumulator = {
  matched: FileMatch[];
  recordingOnly: StagedRespondentRow[];
  remainingTranscripts: StagedFileCandidate[];
};

@Component({
  selector: 'app-add-respondent',
  templateUrl: './add-respondent.component.html',
  styleUrls: ['./add-respondent.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'block',
  },
  standalone: false,
})
export class AddRespondentComponent implements OnInit {
  private uploadErrorMessage =
    'We could not create this expert. Please review this row and retry.';

  private commonFileNameTokens = new Set([
    'audio',
    'call',
    'captions',
    'enhanced',
    'final',
    'meeting',
    'recording',
    'speaker',
    'speakers',
    'team',
    'teams',
    'transcript',
    'video',
    'zoom',
  ]);
  private rowUploadErrors = new WeakMap<AddRespondentRowFormGroup, string>();

  addRespondentFormGroup: AddRespondentFormGroup =
    new FormGroup<AddRespondentFormControls>({
      compliance: new FormControl(false, {
        nonNullable: true,
        validators: [Validators.requiredTrue],
      }),
    });
  respondentsFormGroup: AddRespondentRowsFormGroup =
    this.addRespondentRowFormService.createRowsFormGroup();

  segments: IDataCaptureSegment[] = [];
  unassignedFiles: UnassignedFile[] = [];
  bulkImportSummary: BulkImportSummary | null = null;

  loading = true;
  uploading = false;
  validating = false;
  bulkStaging = false;
  bulkDragActive = false;
  bulkUploadStep: BulkUploadStep = 'upload';

  addRespondentFormGroupSubmitted = false;

  rowUploadErrorLookup = (row: AddRespondentRowFormGroup) =>
    this.getRowUploadError(row);

  clearRowUploadErrorLookup = (row: AddRespondentRowFormGroup) =>
    this.clearRowUploadError(row);

  get complianceControl() {
    return this.addRespondentFormGroup.controls.compliance;
  }

  get respondentsArray() {
    return this.respondentsFormGroup.controls.respondents;
  }

  get canSubmit() {
    return !!this.respondentsArray.length;
  }

  get isBulkUploadStep() {
    return this.bulkUploadStep === 'upload';
  }

  get isBulkReviewStep() {
    return this.bulkUploadStep === 'review';
  }

  constructor(
    private dataCaptureService: DataCaptureService,
    private externalCallDurationValidationService: ExternalCallDurationValidationService,
    private standardiseTranscriptService: StandardiseTranscriptService,
    private addRespondentRowFormService: AddRespondentRowFormService,
    private dialogRef: MatDialogRef<AddRespondentComponent>,
    private toastService: ToastrService,
    private cdr: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) private opportunityId: string
  ) {}

  ngOnInit() {
    this.dataCaptureService
      .getAvailableSegments(this.opportunityId)
      .subscribe((res) => {
        this.segments = res;
        this.loading = false;
        this.cdr.markForCheck();
      });
  }

  goToBulkUploadStep() {
    this.bulkUploadStep = 'upload';
  }

  goToBulkReviewStep() {
    this.bulkUploadStep = 'review';
  }

  onBulkFilesChange(target: HTMLInputElement) {
    const files = Array.from(target?.files || []);

    this.stageBulkFiles(files);

    if (target) {
      target.value = '';
    }
  }

  onBulkDragOver(event: DragEvent) {
    event.preventDefault();
    this.bulkDragActive = true;
  }

  onBulkDragLeave(event: DragEvent) {
    event.preventDefault();
    this.bulkDragActive = false;
  }

  onBulkDrop(event: DragEvent) {
    event.preventDefault();
    this.bulkDragActive = false;

    const files = Array.from(event.dataTransfer?.files || []);

    this.stageBulkFiles(files);
  }

  submit() {
    if (this.uploading || this.validating) {
      return;
    }

    if (!this.respondentsArray.length) {
      this.toastService.warning(
        'Stage files or add at least one expert before uploading.',
        'Nothing uploaded'
      );
      return;
    }

    this.addRespondentFormGroupSubmitted = true;

    this.validating = true;

    this.respondentsArray.controls.forEach((row) => {
      this.clearRowUploadError(row);
      row.updateValueAndValidity();
      row.markAllAsTouched();
    });

    this.addRespondentFormGroup.updateValueAndValidity();
    this.addRespondentFormGroup.markAllAsTouched();
    this.respondentsFormGroup.updateValueAndValidity();

    const waitForValidation$ = this.respondentsFormGroup.pending
      ? this.respondentsFormGroup.statusChanges.pipe(
          filter((s) => s !== 'PENDING'),
          take(1),
          map(() => null)
        )
      : of(null);

    waitForValidation$
      .pipe(
        tap(() => {
          this.validating = false;
          this.cdr.markForCheck();
        }),
        filter(() => this.complianceControl.valid),
        map(() => this.respondentsArray.controls.filter((row) => row.valid)),
        filter((validRows) => validRows.length > 0),
        tap(() => {
          this.uploading = true;
          this.cdr.markForCheck();
        }),
        switchMap((validRows) => this.uploadRespondents(validRows)),
        tap((results) => this.handleUploadResults(results)),
        finalize(() => {
          this.uploading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe();
  }

  getRowUploadError(row: AddRespondentRowFormGroup) {
    return this.rowUploadErrors.get(row) || null;
  }

  onAddExpertWithoutFiles() {
    this.respondentsArray.push(this.addRespondentRowFormService.create());
    this.goToBulkReviewStep();
  }

  clearUnassignedFiles() {
    this.unassignedFiles = [];
    this.bulkImportSummary = this.bulkImportSummary
      ? {
          ...this.bulkImportSummary,
          unassignedCount: 0,
        }
      : null;
  }

  private generateUniqueId() {
    return crypto.randomUUID();
  }

  private uploadRespondents(respondents: AddRespondentRowFormGroup[]) {
    return respondents.length
      ? from(respondents).pipe(
          concatMap((row) => this.uploadSingleRespondent(row)),
          toArray()
        )
      : of([]);
  }

  private uploadSingleRespondent(row: AddRespondentRowFormGroup) {
    const m4aFile = row.controls.m4aFile.value;
    const transcriptFile = row.controls.transcriptFile.value;

    const audioExtension = m4aFile?.name?.toLowerCase().split('.').pop();
    const respondentUid = this.generateUniqueId();

    const respondent = {
      firstName: row.controls.firstName.value,
      lastName: row.controls.lastName.value,
      segment: row.controls.segment.value,
      dateOfCall: row.controls.dateOfCall.value,
      source: row.controls.source.value,
    };

    if (!m4aFile && !transcriptFile) {
      return of<UploadResult>({ row, status: 'failed' });
    }

    if (m4aFile && !audioExtension) {
      return of<UploadResult>({ row, status: 'failed' });
    }

    const standardisedTranscript$ = transcriptFile
      ? this.standardiseTranscriptService.standardise(transcriptFile)
      : of(null);

    return standardisedTranscript$.pipe(
      switchMap((sTranscript) =>
        this.dataCaptureService
          .uploadExternalConnectionFile({
            externalRespondentId: respondentUid,
            opportunitySegmentId: respondent.segment,
            audioExtension,
          })
          .pipe(
            switchMap((res) =>
              combineLatest([
                m4aFile
                  ? this.dataCaptureService
                      .uploadFile(m4aFile, res.audio.uploadUrl)
                      .pipe(map(() => res.audio.fileKey))
                  : of(null),
                sTranscript
                  ? this.dataCaptureService
                      .uploadFile(sTranscript, res.transcript.uploadUrl)
                      .pipe(map(() => res.transcript.fileKey))
                  : of(null),
              ])
            ),
            switchMap(([audioFileKey, transcriptFileKey]) =>
              this.dataCaptureService.createExternalConnection({
                external_connection_id: respondentUid,
                opportunity_id: this.opportunityId,
                opportunity_segment_id: respondent.segment,
                first_name: respondent.firstName,
                last_name: respondent.lastName,
                call_datetime: respondent.dateOfCall,
                source: respondent.source,
                compliance_accepted: true,
                ...(audioFileKey ? { audioFileKey } : {}),
                ...(transcriptFileKey ? { transcriptFileKey } : {}),
              })
            )
          )
      ),
      map(() => ({ row, status: 'success' })),
      catchError(() => {
        this.setRowUploadError(row, this.uploadErrorMessage);
        return of<UploadResult>({ row, status: 'failed' });
      })
    );
  }

  private setRowUploadError(row: AddRespondentRowFormGroup, message: string) {
    this.rowUploadErrors.set(row, message);
  }

  private clearRowUploadError(row: AddRespondentRowFormGroup) {
    this.rowUploadErrors.delete(row);
  }

  private handleUploadResults(results: UploadResult[]) {
    const successfulRows = results
      .filter((result) => result.status === 'success')
      .map((result) => result.row);

    const failedCount = results.filter(
      (result) => result.status === 'failed'
    ).length;

    const invalidCount = this.respondentsArray.controls.filter(
      (row) => row.invalid
    ).length;

    if (!failedCount && !invalidCount) {
      this.dialogRef.close(true);
      return;
    }

    this.removeSuccessfulRows(successfulRows);

    if (failedCount) {
      this.toastService.error(
        'Some experts could not be uploaded. Review the highlighted rows and retry.',
        'Upload incomplete'
      );
    } else if (successfulRows.length && invalidCount) {
      this.toastService.warning(
        'Some rows need attention before they can be uploaded.',
        'Upload incomplete'
      );
    }
  }

  private removeSuccessfulRows(successfulRows: AddRespondentRowFormGroup[]) {
    for (let i = this.respondentsArray.length - 1; i >= 0; i--) {
      const row = this.respondentsArray.at(i);

      if (successfulRows.includes(row)) {
        this.respondentsArray.removeAt(i);
      }
    }
  }

  private stageBulkFiles(files: File[]) {
    if (!files.length) {
      return;
    }

    this.bulkStaging = true;

    const audioFiles = files.filter((file) => this.isAudioFile(file));
    const transcriptFiles = files.filter((file) => this.isTranscriptFile(file));

    this.unassignedFiles = files
      .filter((file) => !this.isAudioFile(file) && !this.isTranscriptFile(file))
      .map((file) => ({
        file,
        reason:
          'Unsupported file type. Only .m4a, .mp3, .mp4, and .vtt files can be staged.',
      }));

    this.buildStagedRows(audioFiles, transcriptFiles)
      .pipe(
        finalize(() => {
          this.bulkStaging = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe((stagedRows) => {
        this.applyStagedRows(stagedRows);

        this.bulkImportSummary = {
          audioCount: audioFiles.length,
          transcriptCount: transcriptFiles.length,
          rowsCreated: stagedRows.length,
          unassignedCount: this.unassignedFiles.length,
        };

        this.goToBulkReviewStep();
      });
  }

  private matchTranscripts(
    audioFiles: File[],
    transcriptFiles: File[]
  ): TranscriptMatchResult {
    const audioCandidates = audioFiles.map((file) =>
      this.buildStagedFileCandidate(file)
    );
    const transcriptCandidates = transcriptFiles.map((file) =>
      this.buildStagedFileCandidate(file)
    );

    const { matched, recordingOnly, remainingTranscripts } =
      audioCandidates.reduce<MatchAccumulator>(
        (acc, audioCandidate) => {
          const bestMatch = this.findBestTranscriptMatch(
            audioCandidate,
            acc.remainingTranscripts
          );

          if (!bestMatch) {
            return {
              ...acc,
              recordingOnly: [
                ...acc.recordingOnly,
                this.buildRecordingOnlyRow(audioCandidate.file),
              ],
            };
          }

          return {
            ...acc,
            matched: [
              ...acc.matched,
              {
                audioCandidate,
                transcriptCandidate: bestMatch.transcriptCandidate,
                score: bestMatch.score,
              },
            ],
            remainingTranscripts: acc.remainingTranscripts.filter(
              (candidate) => candidate !== bestMatch.transcriptCandidate
            ),
          };
        },
        {
          matched: [],
          recordingOnly: [],
          remainingTranscripts: transcriptCandidates,
        }
      );

    const transcriptOnly = remainingTranscripts.map((candidate) =>
      this.buildTranscriptOnlyRow(candidate.file)
    );

    return { matched, recordingOnly, transcriptOnly };
  }

  private findBestTranscriptMatch(
    audioCandidate: StagedFileCandidate,
    availableTranscripts: StagedFileCandidate[]
  ) {
    const candidateScores = availableTranscripts
      .map((transcriptCandidate) => ({
        transcriptCandidate,
        score: this.scoreTranscriptMatch(audioCandidate, transcriptCandidate),
      }))
      .sort((a, b) => b.score - a.score);

    const [bestCandidate, secondBestCandidate] = candidateScores;

    const isConfidentMatch =
      bestCandidate &&
      bestCandidate.score >= 24 &&
      (!secondBestCandidate ||
        bestCandidate.score - secondBestCandidate.score >= 8);

    return isConfidentMatch ? bestCandidate : null;
  }

  private buildRecordingOnlyRow(audioFile: File): StagedRespondentRow {
    return {
      audioFile,
      dateOfCall: this.resolveSuggestedDate(audioFile),
      matchStatus: 'recordingOnly',
      matchMessage:
        'Recording staged. Add a transcript if you have one, then review the row before upload.',
    };
  }

  private buildTranscriptOnlyRow(transcriptFile: File): StagedRespondentRow {
    return {
      transcriptFile,
      dateOfCall: this.resolveSuggestedDate(transcriptFile),
      matchStatus: 'transcriptOnly',
      matchMessage:
        'Transcript staged with no recording. Review the row before upload.',
    };
  }

  private buildStagedRows(audioFiles: File[], transcriptFiles: File[]) {
    const { matched, recordingOnly, transcriptOnly } = this.matchTranscripts(
      audioFiles,
      transcriptFiles
    );

    if (!matched.length) {
      return of([...recordingOnly, ...transcriptOnly]);
    }

    return forkJoin(
      matched.map(({ audioCandidate, transcriptCandidate }) =>
        this.externalCallDurationValidationService.validate(
          transcriptCandidate.file,
          audioCandidate.file
        )
      )
    ).pipe(
      map((validationResults) => {
        const matchedRows = matched.map((match, index) =>
          this.buildMatchedRow(match, validationResults[index])
        );

        return [...matchedRows, ...recordingOnly, ...transcriptOnly];
      })
    );
  }

  private buildMatchedRow(
    match: FileMatch,
    durationValidation: { durationMismatch?: boolean } | null
  ): StagedRespondentRow {
    const { audioCandidate, transcriptCandidate, score } = match;
    const hasDurationMismatch = !!durationValidation?.durationMismatch;
    const needsReview = hasDurationMismatch || score < 60;

    return {
      audioFile: audioCandidate.file,
      transcriptFile: transcriptCandidate.file,
      dateOfCall: this.resolveSuggestedDate(audioCandidate.file),
      matchStatus: needsReview ? 'needsReview' : 'matched',
      matchMessage: hasDurationMismatch
        ? 'Transcript and recording were paired by filename, but their durations do not line up. Check this pair before upload.'
        : score < 60
          ? 'Potential transcript match found from the filename. Review this pair before upload.'
          : 'Transcript and recording were paired automatically. Review and confirm the row before upload.',
    };
  }

  private applyStagedRows(stagedRows: StagedRespondentRow[]) {
    if (!stagedRows.length) {
      return;
    }

    const primaryRow = this.respondentsArray.at(0) || null;

    const shouldReusePrimaryRow =
      primaryRow &&
      this.respondentsArray.length === 1 &&
      this.addRespondentRowFormService.isRowBlank(primaryRow);

    stagedRows.forEach((stagedRow, index) => {
      const rowValues: Partial<AddRespondentRowFormValue> = {
        dateOfCall: stagedRow.dateOfCall,
        m4aFile: stagedRow.audioFile || null,
        transcriptFile: stagedRow.transcriptFile || null,
        matchStatus: stagedRow.matchStatus,
        matchMessage: stagedRow.matchMessage,
      };

      if (shouldReusePrimaryRow && !index) {
        primaryRow.patchValue(rowValues);
        primaryRow.updateValueAndValidity();
        return;
      }

      this.respondentsArray.push(
        this.addRespondentRowFormService.create(rowValues)
      );
    });
  }

  private buildStagedFileCandidate(file: File): StagedFileCandidate {
    const rawStem = this.stripExtension(file.name);
    const tokens = this.tokeniseFileStem(rawStem);

    return {
      file,
      rawStem,
      tokens,
      matchKey: tokens.join(' '),
      dateKey: this.extractDateKey(file.name) || this.formatFileDate(file),
    };
  }

  private scoreTranscriptMatch(
    audioCandidate: StagedFileCandidate,
    transcriptCandidate: StagedFileCandidate
  ) {
    const sharedTokens = audioCandidate.tokens.filter((token) =>
      transcriptCandidate.tokens.includes(token)
    );

    let score = sharedTokens.length * 12;

    if (
      audioCandidate.matchKey &&
      audioCandidate.matchKey === transcriptCandidate.matchKey
    ) {
      score += 60;
    }

    if (
      audioCandidate.rawStem.toLowerCase() ===
      transcriptCandidate.rawStem.toLowerCase()
    ) {
      score += 20;
    }

    if (
      audioCandidate.dateKey &&
      audioCandidate.dateKey === transcriptCandidate.dateKey
    ) {
      score += 16;
    }

    if (
      sharedTokens.length &&
      audioCandidate.tokens[0] &&
      audioCandidate.tokens[0] === transcriptCandidate.tokens[0]
    ) {
      score += 6;
    }

    return score;
  }

  private isAudioFile(file: File) {
    return this.addRespondentRowFormService.isAudioFile(file);
  }

  private isTranscriptFile(file: File) {
    return this.addRespondentRowFormService.isTranscriptFile(file);
  }

  private stripExtension(fileName: string) {
    return fileName.replace(/\.[^.]+$/, '');
  }

  private tokeniseFileStem(fileStem: string) {
    return fileStem
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .split(' ')
      .filter((token) => token.length > 1)
      .filter((token) => !this.commonFileNameTokens.has(token))
      .filter((token) => !/^\d+$/.test(token));
  }

  private extractDateKey(fileName: string) {
    const stem = this.stripExtension(fileName);
    const yearFirstMatch = stem.match(
      /(20\d{2})[-_. ]?(0[1-9]|1[0-2])[-_. ]?(0[1-9]|[12]\d|3[01])/
    );

    if (yearFirstMatch) {
      return [yearFirstMatch[1], yearFirstMatch[2], yearFirstMatch[3]].join(
        '-'
      );
    }

    const compactMatch = stem.match(
      /(?:^|[^0-9])(20\d{2})(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])(?:[^0-9]|$)/
    );

    if (compactMatch) {
      return [compactMatch[1], compactMatch[2], compactMatch[3]].join('-');
    }

    return '';
  }

  private resolveSuggestedDate(file: File) {
    const extractedDate = this.extractDateKey(file.name);

    if (extractedDate) {
      return `${extractedDate}T09:00`;
    }

    return this.formatFileDate(file);
  }

  private formatFileDate(file: File) {
    if (!file.lastModified) {
      return '';
    }

    const date = new Date(file.lastModified);
    const pad = (value: number) => value.toString().padStart(2, '0');

    return [date.getFullYear(), pad(date.getMonth() + 1), pad(date.getDate())]
      .join('-')
      .concat(`T${pad(date.getHours())}:${pad(date.getMinutes())}`);
  }
}
